import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { H2 } from "components/ui/typography/titles/Titles"
import { TableQuotations } from "./layout/TableQuotations/TableQuotations"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { useGetQuotations } from "hooks/api/quotation/quotations.hook"
import quotationsService from "services/quotations.service"
import { useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { QuotationViewerModal } from "components/ui/customWebviewer/QuotationViewerModal"
import type { TQuotationViewerMode } from "components/ui/customWebviewer/QuotationViewerModal"
import { useDocumentDownload } from "hooks/api/downloadFIle/documentDownload.hook"
import { useDocuments } from "hooks/api/drawings/drawings.hook"
import { usePresignedDownloadUrl } from "hooks/api/downloadFIle/downloadFIle.hook"
import { removeApiVersion } from "helpers/utils.helper"
import { CreateQuotationModal } from "./layout/CreateQuotationModal/CreateQuotationModal"
import { RenameQuotationModal } from "./layout/RenameQuotationModal/RenameQuotationModal"
import { QuotationSettingsModal } from "./layout/QuotationSettingsModal/QuotationSettingsModal"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { Modal } from "components/ui/modals/Modal/Modal"
import { FeedbackPanel } from "components/ui/FeedbackPanel/FeedbackPanel"
import { useViewerStore } from "store/viewer.store"
import type { TDrawingStatus } from "types/drawer.type"
import style from "./Quotations.module.scss"

const {
	quotationsStyles,
	header,
	title,
	closeButton,
	closeIcon,
	createQuotationButton,
	buttonWrapper,
	tableWrapper,
} = style

type Props = {
	onCloseDrawer: () => void
	drawingName: string
	drawingStatus: TDrawingStatus
	fileOwner: "me" | "other"
}

export const Quotations = ({ onCloseDrawer, drawingName, drawingStatus, fileOwner }: Props) => {
	const { downloadHandle } = useDocumentDownload()
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { isQuotationFeedbackOpen, closeQuotationFeedback, isQuotationSettingsOpen, closeQuotationSettings } =
		useViewerStore()

	const [searchParams, setSearchParams] = useSearchParams()

	const [isCreateQuotationModalOpen, setCreateQuotationModalOpen] = useState(false)
	// Single shared Apryse viewer: "preview" (read-only) and "editor" reuse ONE instance (#1119).
	const [viewerMode, setViewerMode] = useState<TQuotationViewerMode>("closed")
	const [loadingQuotationId, setLoadingQuotationId] = useState<number | null>(null)
	const editRequestRef = useRef(0)
	const isMountedRef = useRef(true)

	useEffect(() => {
		isMountedRef.current = true
		return () => {
			isMountedRef.current = false
		}
	}, [])

	// Close any open feedback/settings modal when this tab unmounts so neither leaks into other tabs
	// (the flags are global, so without this a stale-open flag would re-open an orphan modal on remount).
	// The viewer modal is driven by local `viewerMode` state, so it tears down with the component.
	useEffect(() => {
		return () => {
			closeQuotationFeedback()
			closeQuotationSettings()
		}
	}, [closeQuotationFeedback, closeQuotationSettings])

	const [renameQuotationState, setRenameQuotationState] = useState<{
		isOpen: boolean
		quotationId: number | null
		quotationName: string
	}>({
		isOpen: false,
		quotationId: null,
		quotationName: "",
	})

	const drawingId = Number(searchParams.get("drawingID"))
	const processingId = Number(searchParams.get("processingID"))

	const { data: quotations } = useGetQuotations(drawingId)
	const isNoQuotations = quotations?.length === 0
	const userIsNotFileOwner = fileOwner === "other"

	const { data: documentsData } = useDocuments(drawingId, processingId, drawingStatus)
	const originalDocEndpoint = useMemo(() => {
		const originalDoc = documentsData?.documents?.find((doc: { note: string }) => doc.note === "Original")
		return originalDoc ? removeApiVersion(originalDoc.download_endpoint) : null
	}, [documentsData])
	const { data: originalPdfData } = usePresignedDownloadUrl(originalDocEndpoint)

	const processingUrl = window.location.href
	// Closing the viewer also hides the feedback modal, but the FeedbackPanel stays mounted
	// (see below) so any in-progress draft survives until the feedback is actually sent.
	// The settings modal is also dismissed so it can't linger over a closed viewer.
	const closeViewer = useCallback(() => {
		setViewerMode("closed")
		closeQuotationFeedback()
		closeQuotationSettings()
	}, [closeQuotationFeedback, closeQuotationSettings])

	const handleEditQuotation = async (quotationId: number) => {
		if (loadingQuotationId !== null) return
		const requestId = ++editRequestRef.current
		setLoadingQuotationId(quotationId)
		try {
			await queryClient.ensureQueryData({
				queryKey: ["quotation", quotationId],
				queryFn: () => quotationsService.getQuotation(quotationId),
			})
		} catch (error) {
			if (isMountedRef.current && editRequestRef.current === requestId) {
				setLoadingQuotationId(null)
			}
			formatError(error)
			return
		}
		if (!isMountedRef.current || editRequestRef.current !== requestId) return
		// Set quotationID in the URL so the row unfolds (collapsing any other expanded row) and
		// every customWebviewer reader (sidebars, save-hooks) picks up the right id. Then open
		// the editor — data is already prefetched into the React Query cache above.
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				next.set("quotationID", String(quotationId))
				return next
			},
			{ replace: true },
		)
		setLoadingQuotationId(null)
		setViewerMode("editor")
	}

	return (
		<div className={quotationsStyles}>
			<div className={header}>
				<H2 className={title}>{drawingName}</H2>
				<button className={closeButton} onClick={onCloseDrawer}>
					<CloseIcon className={closeIcon} />
				</button>
			</div>
			<div className={tableWrapper}>
				<TableQuotations
					userIsNotFileOwner={userIsNotFileOwner}
					onDownload={downloadHandle}
					onPreview={() => setViewerMode("preview")}
					createQuotation={() => setCreateQuotationModalOpen(true)}
					renameQuotation={(quotationId: number) => {
						const quotation = quotations?.find((q) => q.id === quotationId)
						setRenameQuotationState({
							isOpen: true,
							quotationId,
							quotationName: quotation?.name || "",
						})
					}}
					editQuotation={handleEditQuotation}
					loadingQuotationId={loadingQuotationId}
					isNoQuotations={isNoQuotations}
				/>
			</div>
			{!isNoQuotations && !userIsNotFileOwner && (
				<div className={buttonWrapper}>
					<ButtonPrimary onClick={() => setCreateQuotationModalOpen(true)} className={createQuotationButton}>
						Create quotation
					</ButtonPrimary>
				</div>
			)}
			<CreateQuotationModal
				isModalOpen={isCreateQuotationModalOpen}
				setModalClose={() => setCreateQuotationModalOpen(false)}
			/>
			<RenameQuotationModal
				isModalOpen={renameQuotationState.isOpen}
				setModalClose={() => setRenameQuotationState({ isOpen: false, quotationId: null, quotationName: "" })}
				quotationId={renameQuotationState.quotationId}
				quotationName={renameQuotationState.quotationName}
			/>
			<QuotationSettingsModal
				isModalOpen={isQuotationSettingsOpen}
				setModalClose={closeQuotationSettings}
				quotationId={Number(searchParams.get("quotationID")) || undefined}
			/>
			<QuotationViewerModal
				mode={viewerMode}
				onClose={closeViewer}
				fileLink={originalPdfData?.download_url}
				drawingId={drawingId}
				processingId={processingId}
				processingUrl={processingUrl}
				drawingStatus={drawingStatus}
			/>
			{/*
			 * FeedbackPanel is rendered un-gated (not behind `isQuotationFeedbackOpen &&`) so that
			 * Ant Design keeps it mounted after the first open. That preserves the typed feedback and
			 * attached screenshots while the modal is closed, until the user actually sends them.
			 */}
			<Modal
				isModalOpen={isQuotationFeedbackOpen}
				setModalClose={closeQuotationFeedback}
				closable={false}
				width={560}
				styles={{ content: { padding: 0 }, body: { padding: 0, maxHeight: "85vh", overflow: "auto" } }}
			>
				<FeedbackPanel
					variant="white"
					onClose={closeQuotationFeedback}
					drawingId={drawingId}
					processingId={Number.isFinite(processingId) ? processingId : null}
					processingUrl={processingUrl}
					drawingStatus={drawingStatus}
				/>
			</Modal>
		</div>
	)
}
